import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { callSealSeekAPI, getRefusalCategory } from '../services/qianwenAPI';
import { safetyRegressionCases } from '../data/safetyRegressionCases';
import './QuestionTester.css';

const QuestionTester = ({ onClose }) => {
  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [summary, setSummary] = useState(null);

  // 处理文件选择
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // 验证文件类型
      const fileType = selectedFile.name.split('.').pop().toLowerCase();
      if (!['xlsx', 'xls'].includes(fileType)) {
        alert('请上传Excel文件（.xlsx或.xls格式）');
        return;
      }
      setFile(selectedFile);
      setResults([]);
      setProgress({ current: 0, total: 0 });
      setSummary(null);
    }
  };

  const evaluateExpected = (answer, expected) => {
    const refusalCategory = getRefusalCategory(answer);
    if (expected === 'allow') {
      return refusalCategory === null;
    }
    return refusalCategory === expected;
  };

  // 处理Excel文件
  const processExcel = async () => {
    if (!file) {
      alert('请先选择文件');
      return;
    }

    setProcessing(true);
    setResults([]);
    setSummary(null);

    try {
      // 读取Excel文件
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      // 获取数据范围
      const range = XLSX.utils.decode_range(worksheet['!ref']);
      const questions = [];

      // 读取所有问题（从第2行开始，跳过标题行）
      for (let row = 1; row <= range.e.r; row++) {
        const typeCell = worksheet[`A${row + 1}`];
        const questionCell = worksheet[`B${row + 1}`];

        if (questionCell && questionCell.v) {
          questions.push({
            row: row + 1,
            type: typeCell && typeCell.v ? typeCell.v.toString() : '',
            question: questionCell.v.toString(),
            answer: ''
          });
        }
      }

      setProgress({ current: 0, total: questions.length });

      // 逐个调用API获取答案
      const processedResults = [];
      for (let i = 0; i < questions.length; i++) {
        const item = questions[i];
        setCurrentQuestion(item.question);
        setProgress({ current: i + 1, total: questions.length });

        try {
          const answer = await callSealSeekAPI(item.question);
          item.answer = answer;
          processedResults.push({ ...item, status: 'success' });

          // 写入答案到worksheet
          const answerCell = `C${item.row}`;
          if (!worksheet[answerCell]) {
            worksheet[answerCell] = {};
          }
          worksheet[answerCell].v = answer;
          worksheet[answerCell].t = 's';

          // 延迟1秒，避免请求过快
          if (i < questions.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        } catch (error) {
          console.error('API调用失败:', error);
          item.answer = `错误: ${error.message}`;
          processedResults.push({ ...item, status: 'error' });

          // 写入错误信息
          const answerCell = `C${item.row}`;
          if (!worksheet[answerCell]) {
            worksheet[answerCell] = {};
          }
          worksheet[answerCell].v = item.answer;
          worksheet[answerCell].t = 's';
        }
      }

      setResults(processedResults);
      setCurrentQuestion('');

      // 生成并下载结果文件
      const outputWorkbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(outputWorkbook, worksheet, sheetName);

      const fileName = file.name.replace(/\.xlsx?$/i, '_answered.xlsx');
      XLSX.writeFile(outputWorkbook, fileName);

      alert(`✅ 处理完成！共处理 ${questions.length} 个问题\n文件已下载: ${fileName}`);
    } catch (error) {
      console.error('处理Excel文件失败:', error);
      alert(`处理失败: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const runRegressionSuite = async () => {
    setProcessing(true);
    setResults([]);
    setCurrentQuestion('');
    setProgress({ current: 0, total: safetyRegressionCases.length });
    setSummary(null);

    try {
      const regressionResults = [];

      for (let i = 0; i < safetyRegressionCases.length; i++) {
        const item = safetyRegressionCases[i];
        setCurrentQuestion(item.question);
        setProgress({ current: i + 1, total: safetyRegressionCases.length });

        try {
          const answer = await callSealSeekAPI(item.question);
          const passed = evaluateExpected(answer, item.expected);

          regressionResults.push({
            ...item,
            answer,
            status: passed ? 'pass' : 'fail'
          });
        } catch (error) {
          regressionResults.push({
            ...item,
            answer: `错误: ${error.message}`,
            status: 'error'
          });
        }

        if (i < safetyRegressionCases.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      const passedCount = regressionResults.filter(item => item.status === 'pass').length;
      const failedCount = regressionResults.filter(item => item.status === 'fail').length;
      const errorCount = regressionResults.filter(item => item.status === 'error').length;

      setResults(regressionResults);
      setSummary({
        mode: 'regression',
        passed: passedCount,
        failed: failedCount,
        errors: errorCount,
        total: regressionResults.length
      });
      setCurrentQuestion('');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="question-tester-overlay" onClick={onClose}>
      <div className="question-tester-modal" onClick={(e) => e.stopPropagation()}>
        <div className="question-tester-header">
          <h2>批量测试题目</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="question-tester-content">
          {/* 文件上传区域 */}
          <div className="upload-section">
            <div className="upload-area">
              <input
                type="file"
                id="file-input"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                disabled={processing}
                style={{ display: 'none' }}
              />
              <label htmlFor="file-input" className="upload-label">
                <div className="upload-icon">📄</div>
                <div className="upload-text">
                  {file ? file.name : '点击选择Excel文件'}
                </div>
                <div className="upload-hint">
                  支持 .xlsx 和 .xls 格式
                </div>
              </label>
            </div>

            <div className="file-format-hint">
              <strong>Excel格式要求：</strong>
              <div>A列：类型 | B列：问题 | C列：答案（自动填充）</div>
              <div>第1行为表头，从第2行开始为数据</div>
            </div>

            <div className="tester-actions">
              <button
                className="process-btn"
                onClick={processExcel}
                disabled={!file || processing}
              >
                {processing ? '处理中...' : '开始处理'}
              </button>
              <button
                className="process-btn regression-btn"
                onClick={runRegressionSuite}
                disabled={processing}
              >
                {processing ? '处理中...' : '运行内置风控回归'}
              </button>
            </div>
          </div>

          {/* 进度显示 */}
          {processing && (
            <div className="progress-section">
              <div className="progress-bar-container">
                <div
                  className="progress-bar"
                  style={{
                    width: `${(progress.current / progress.total) * 100}%`
                  }}
                />
              </div>
              <div className="progress-text">
                进度: {progress.current} / {progress.total}
              </div>
              {currentQuestion && (
                <div className="current-question">
                  正在处理: {currentQuestion}
                </div>
              )}
            </div>
          )}

          {/* 结果预览 */}
          {results.length > 0 && (
            <div className="results-section">
              <h3>处理结果预览</h3>
              {summary && summary.mode === 'regression' && (
                <div className="results-summary">
                  通过 {summary.passed} 条，失败 {summary.failed} 条，异常 {summary.errors} 条，共 {summary.total} 条
                </div>
              )}
              <div className="results-list">
                {results.slice(0, 5).map((item, index) => (
                  <div key={index} className={`result-item ${item.status}`}>
                    <div className="result-header">
                      <span className="result-type">{item.type}</span>
                      <span className="result-status">
                        {item.status === 'success' || item.status === 'pass' ? '✅' : '❌'}
                      </span>
                    </div>
                    <div className="result-question">Q: {item.question}</div>
                    {item.expected && (
                      <div className="result-expected">期望: {item.expected}</div>
                    )}
                    <div className="result-answer">
                      A: {item.answer.substring(0, 100)}
                      {item.answer.length > 100 ? '...' : ''}
                    </div>
                  </div>
                ))}
                {results.length > 5 && (
                  <div className="more-results">
                    还有 {results.length - 5} 个结果...
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionTester;
